import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_BASE = "https://wa.me/919891315777?text=";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

export default function AreaPage({ area }) {
  const whatsappUrl = `${WHATSAPP_BASE}${encodeURIComponent(`Hi, I want to book water tank cleaning in ${area.name}, Gurugram`)}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`Water Tank Cleaning in ${area.name}, Gurugram | TankBuddy`}</title>
        <meta name="description" content={`Professional water tank cleaning service in ${area.name}, Gurugram. Overhead & underground tanks. Same-day service. Certified technicians. Starting ₹999. Call TankBuddy!`} />
        <meta name="keywords" content={`water tank cleaning ${area.name}, tank cleaning ${area.name} Gurugram, ${area.name} water tank service`} />
        <link rel="canonical" href={`https://www.tankbuddy.in/${area.slug}`} />
        <meta property="og:title" content={`Water Tank Cleaning in ${area.name}, Gurugram | TankBuddy`} />
        <meta property="og:description" content={`Professional water tank cleaning in ${area.name}, Gurugram. Starting ₹999. Same-day service.`} />
        <meta property="og:url" content={`https://www.tankbuddy.in/${area.slug}`} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Water Tank Cleaning in ${area.name}, Gurugram",
            "provider": {
              "@type": "LocalBusiness",
              "name": "TankBuddy",
              "telephone": "+91-9891315777",
              "url": "https://www.tankbuddy.in",
              "address": {"@type": "PostalAddress", "addressLocality": "Gurugram", "addressRegion": "Haryana", "addressCountry": "IN"}
            },
            "areaServed": "${area.name}, Gurugram",
            "description": "${area.description}"
          }
        `}</script>
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            <MapPin className="w-4 h-4" /> Serving {area.name}, Gurugram
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            {area.h1}
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">{area.content1}</p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">{area.content2}</p>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Tank Cleaning in {area.name}</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting ₹999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777"><Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777</a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in {area.name}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          {area.nearby && area.nearby.length > 0 && (
            <div>
              <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
              <div className="flex flex-wrap gap-2">
                {area.nearby.map(a => (
                  <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                    <MapPin className="w-3 h-3 text-sky" />{a.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
